import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { Phone, Loader2, RefreshCw } from 'lucide-react';
import { AddCallToCrmModal } from './AddCallToCrmModal';
import { useZohoSession } from '../contexts/ZohoSessionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safety-net: 45-min interval keeps the bar accurate without hammering the
// Zoho API. SSE / c30:calls-changed is the primary trigger for instant updates.
const SAFETY_NET_INTERVAL_MS = 45 * 60 * 1000;

// Cross-component event emitted whenever a call is created, completed,
// rescheduled or deleted from anywhere in the app — lets this bar refetch
// immediately rather than waiting for the next poll tick.
//
// To trigger from anywhere:  window.dispatchEvent(new Event('c30:calls-changed'));
const CALLS_CHANGED_EVENT = 'c30:calls-changed';

/**
 * In-app notification bar that surfaces today's *scheduled* Zoho calls for the
 * current user. Clicking a pill opens `AddCallToCrmModal` pre-populated with
 * the scheduled record — on submit, that record transitions from Scheduled →
 * Completed and the pill disappears.
 *
 * Hidden entirely when the user has no scheduled calls today.
 *
 * Fetch strategy (Zoho-quota-friendly):
 *   1. Sales-team gate — hidden + no Zoho calls for users without a CRM seat.
 *   2. On mount — one fetch when component loads.
 *   3. c30:calls-changed — immediate refetch on any in-app call mutation or SSE push.
 *   4. 45-min safety-net interval — catches Zoho-side changes; only fires when
 *      the tab is visible so hidden tabs don't burn quota.
 *   5. Server-side 60s in-memory cache on `/zoho/my-scheduled-calls` (per user).
 */
export const CallNotificationBar = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeCall, setActiveCall] = useState(null);  // full scheduled-call record
  const { isLinked, isSalesTeam, loading: sessionLoading } = useZohoSession();
  const sseRef = useRef(null);

  const fetchCalls = useCallback(async () => {
    if (!isLinked) { setCalls([]); setLoading(false); return; }
    try {
      const res = await api.get(`${API}/zoho/my-scheduled-calls`);
      setCalls(res.data?.calls || []);
    } catch (_) {
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [isLinked]);

  const manualRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCalls();
    setRefreshing(false);
  }, [fetchCalls]);

  // SSE connection — receives instant push when any call for this user is mutated.
  useEffect(() => {
    if (sessionLoading || !isSalesTeam || !isLinked) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const connect = () => {
      const es = new EventSource(`${API}/calls/sse?token=${encodeURIComponent(token)}`);
      sseRef.current = es;
      es.addEventListener('calls_changed', () => {
        // Silent background fetch — dispatching the event also updates
        // any other listeners (e.g. ManageCallsPage).
        fetchCalls();
        window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
      });
      es.onerror = () => {
        es.close();
        // Auto-reconnect after 5 s if connection drops.
        setTimeout(connect, 5000);
      };
    };
    connect();

    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, [fetchCalls, isLinked, isSalesTeam, sessionLoading]);

  // Fetch on mount, on c30:calls-changed (SSE push or in-app mutation),
  // and on the 45-min safety-net interval. No tab/focus listeners —
  // avoids API calls every time a user switches between browser tabs.
  useEffect(() => {
    if (sessionLoading) return;
    if (!isSalesTeam) { setCalls([]); setLoading(false); return; }
    if (!isLinked) { setLoading(false); return; }

    fetchCalls();

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') fetchCalls();
    }, SAFETY_NET_INTERVAL_MS);

    const handleCallsChanged = () => fetchCalls();
    window.addEventListener(CALLS_CHANGED_EVENT, handleCallsChanged);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener(CALLS_CHANGED_EVENT, handleCallsChanged);
    };
  }, [fetchCalls, isLinked, isSalesTeam, sessionLoading]);

  // After a call is marked completed, drop it from local state immediately so
  // the bar shrinks without waiting for the next poll.
  const handleCompleted = (callId) => {
    setCalls(prev => prev.filter(c => c.id !== callId));
    setActiveCall(null);
    // Notify any other listeners (e.g., Manage Calls page) and refetch.
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  };

  // Same optimistic removal when the user deletes the scheduled call from
  // the modal's footer — avoids waiting for the next 2-min poll.
  const handleDeleted = (callId) => {
    setCalls(prev => prev.filter(c => c.id !== callId));
    setActiveCall(null);
    window.dispatchEvent(new Event(CALLS_CHANGED_EVENT));
  };

  if (loading) return null;
  if (!isSalesTeam) return null;
  if (!calls || calls.length === 0) return null;

  const overdueCount = calls.filter(c => c.is_overdue).length;
  const todayCount = calls.length - overdueCount;

  // Headline copy adapts to the mix of overdue vs today's calls.
  let headline;
  if (overdueCount > 0 && todayCount > 0) {
    headline = `${overdueCount} overdue · ${todayCount} today`;
  } else if (overdueCount > 0) {
    headline = overdueCount === 1 ? '1 overdue scheduled call' : `${overdueCount} overdue scheduled calls`;
  } else {
    headline = todayCount === 1 ? 'You have 1 scheduled call today' : `You have ${todayCount} scheduled calls today`;
  }

  return (
    <>
      {/* Title rendered *outside* the bar so the bar itself is dedicated to the
          call pills. Smaller round logo + centered headline. Headline glows
          red when there are overdue calls. */}
      <div className="flex items-center justify-center gap-2 mb-2 mt-1">
        <div className={`w-5 h-5 rounded-full ${overdueCount > 0 ? 'bg-red-600' : 'bg-purple-600'} flex items-center justify-center shadow-sm`}>
          <Phone className="w-2.5 h-2.5 text-white" />
        </div>
        <div className={`text-sm font-bold ${overdueCount > 0 ? 'text-red-700' : 'text-purple-900'}`} data-testid="call-notification-title">
          {headline}
        </div>
        <button
          type="button"
          onClick={manualRefresh}
          disabled={refreshing}
          title="Refresh scheduled calls"
          className={`ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors disabled:opacity-50 ${
            overdueCount > 0
              ? 'text-red-700 hover:bg-red-100'
              : 'text-purple-700 hover:bg-purple-100'
          }`}
          data-testid="call-notification-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div
        className={`mb-4 rounded-2xl border backdrop-blur-[2px] px-4 py-3 ${
          overdueCount > 0
            ? 'border-red-200/80 bg-red-50/70'
            : 'border-purple-200/80 bg-purple-50/70'
        }`}
        data-testid="call-notification-bar"
      >
        <div className="flex flex-wrap justify-center gap-2">
          {calls.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveCall(c)}
              className={`flex flex-col items-center rounded-2xl bg-white border pl-4 pr-4 pt-2 pb-3 transition-all max-w-[320px] hover:shadow-md ${
                c.is_overdue
                  ? 'border-red-300 hover:border-red-500'
                  : 'border-purple-200 hover:border-purple-400'
              }`}
              data-testid={`call-notification-item-${c.id}`}
            >
              <div className="flex flex-col items-center text-center min-w-0 w-full">
                {c.is_overdue && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 mb-1" data-testid={`call-notification-overdue-${c.id}`}>
                    Overdue
                  </span>
                )}
                <span className="font-semibold text-sm text-gray-900 truncate max-w-full">
                  {c.account?.name || 'Unknown account'}
                </span>
                <span className="text-[11px] text-gray-600 flex items-center gap-1.5">
                  <Phone className="w-2.5 h-2.5" />
                  <span className="truncate">{c.account?.phone || c.phone || 'No number on file'}</span>
                </span>
                {c.description && !c.tag && (
                  <span
                    className="text-[11px] text-gray-500 italic mt-1 line-clamp-2 max-w-full"
                    title={c.description}
                    data-testid={`call-notification-note-${c.id}`}
                  >
                    {c.description}
                  </span>
                )}
                {c.tag && (
                  <span
                    className="mt-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 cursor-default max-w-full truncate"
                    title={c.description ? `${c.description}` : c.tag}
                    data-testid={`call-tag-pill-${c.id}`}
                  >
                    {c.tag}
                  </span>
                )}
              </div>
            </button>
          ))}
          {loading && (
            <div className="inline-flex items-center gap-1.5 text-[11px] text-purple-700">
              <Loader2 className="w-3 h-3 animate-spin" /> Refreshing…
            </div>
          )}
        </div>
      </div>

      <AddCallToCrmModal
        isOpen={!!activeCall}
        onClose={() => setActiveCall(null)}
        scheduledCall={activeCall}
        onCompleted={handleCompleted}
        onDeleted={handleDeleted}
      />
    </>
  );
};

export default CallNotificationBar;
